import React from 'react';
import { Marker, Tooltip, Popup, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import { WrenchIcon, TruckIcon } from 'lucide-react';
import { dailyMaintenanceStatus, getMaintenanceStatusForDay, timePointToDay, maintenanceStatusColors, maintenanceNodes, maintenanceRoutes, nmcStagingZones, fieldMaintenanceTeams } from './maintenanceData';
import { convoyRouteSegments, alternativeRouteSegment } from './convoyData';
// Create custom icon for maintenance status markers
const createMaintenanceStatusIcon = (day: number, percentage: number, status: string) => {
  // Map status to color
  const color = status === 'green' ? '#059669' : status === 'amber' ? '#D97706' : status === 'red' ? '#DC2626' : '#1F2937';
  // Calculate progress bar width based on percentage
  const progressWidth = Math.max(10, Math.min(100, percentage)); // Min 10% for visibility, max 100%
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; width: 100px;">
        <div style="display: flex; align-items: center; background-color: white; padding: 4px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); width: 100%;">
          <div style="font-size: 16px; margin-right: 4px;">🛠️</div>
          <div style="flex-grow: 1; background-color: #f3f4f6; height: 8px; border-radius: 4px; overflow: hidden;">
            <div style="background-color: ${color}; height: 100%; width: ${progressWidth}%;"></div>
          </div>
          <div style="margin-left: 4px; font-weight: bold; font-size: 12px;">${percentage}%</div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          Day ${day}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 50],
    iconAnchor: [50, 25]
  });
};
// Create custom icon for maintenance nodes
const createMaintenanceNodeIcon = (isAlternate: boolean) => {
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 36px; height: 36px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid ${isAlternate ? '#8B5CF6' : '#047857'};">
          <div style="font-size: 18px;">🛠️</div>
        </div>
        <div style="background-color: ${isAlternate ? '#8B5CF6' : '#047857'}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          ${isAlternate ? 'ALT' : 'M1'}
        </div>
      </div>
    `,
    className: '',
    iconSize: [36, 58],
    iconAnchor: [18, 40]
  });
};
// Create custom icon for NMC staging zones
const createNMCStagingZoneIcon = (vehiclesNMC: number) => {
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 36px; height: 36px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid #DC2626;">
          <div style="font-size: 18px;">🚫</div>
        </div>
        <div style="background-color: #DC2626; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          NMC: ${vehiclesNMC}
        </div>
      </div>
    `,
    className: '',
    iconSize: [36, 58],
    iconAnchor: [18, 40]
  });
};
// Create custom icon for field maintenance teams
const createFieldMaintenanceTeamIcon = () => {
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 36px; height: 36px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid #0EA5E9;">
          <div style="font-size: 18px;">🔧</div>
        </div>
        <div style="background-color: #0EA5E9; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          FMT
        </div>
      </div>
    `,
    className: '',
    iconSize: [36, 58],
    iconAnchor: [18, 40]
  });
};
interface MaintenanceLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const MaintenanceLayer: React.FC<MaintenanceLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Convert selected time point to day number
  const currentDay = timePointToDay(selectedTimePoint);
  // Get the route segments based on the selected route
  const routeSegments = convoyRouteSegments.filter(segment => {
    // For day 1, check if we should use the alternative route
    if (segment.day === 1 && useAlternativeRoute) {
      return false;
    }
    return true;
  });
  // Add alternative route if selected
  const allSegments = useAlternativeRoute ? [...routeSegments.filter(segment => segment.day !== 1), alternativeRouteSegment] : routeSegments;
  // Sort segments by day
  const sortedSegments = allSegments.sort((a, b) => a.day - b.day);
  // Filter maintenance nodes based on route
  const visibleNodes = maintenanceNodes.filter(node => useAlternativeRoute ? node.isAlternate : !node.isAlternate);
  // Filter NMC staging zones based on current day
  const visibleNMCZones = nmcStagingZones.filter(zone => zone.day <= currentDay && currentDay !== 0);
  // Filter field maintenance teams based on current day
  const visibleFMTs = fieldMaintenanceTeams.filter(fmt => fmt.day <= currentDay && currentDay !== 0);
  return <>
      {/* Render maintenance nodes */}
      {visibleNodes.map(node => <Marker key={`maintenance-node-${node.id}`} position={node.coordinates} icon={createMaintenanceNodeIcon(node.isAlternate)} data-id="element-235">
          <Popup data-id="element-236">
            <div className="min-w-[200px]" data-id="element-237">
              <h3 className="font-bold text-base" data-id="element-238">{node.name}</h3>
              <p className="text-sm mt-2" data-id="element-239">{node.description}</p>
              <div className="mt-2" data-id="element-240">
                <h4 className="font-medium text-sm" data-id="element-241">Capabilities:</h4>
                <ul className="text-sm list-disc pl-5 mt-1" data-id="element-242">
                  {node.capabilities.map((capability, index) => <li key={index} data-id="element-243">{capability}</li>)}
                </ul>
              </div>
              {currentDay >= 5 && <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-244">
                  <span className="font-medium" data-id="element-245">Full Maintenance Complete</span>{' '}
                  - Vehicle readiness restored to 100%
                </div>}
            </div>
          </Popup>
        </Marker>)}
      {/* Render maintenance routes */}
      {maintenanceRoutes.map(route => <Polyline key={`maintenance-route-${route.id}`} positions={route.coordinates} pathOptions={{
      color: route.type === 'recovery' ? '#DC2626' : '#0EA5E9',
      weight: 3,
      opacity: 0.7,
      dashArray: route.type === 'recovery' ? '10, 5' : '5, 10'
    }} data-id="element-246">
          <Popup data-id="element-247">
            <div data-id="element-248">
              <h3 className="font-bold" data-id="element-249">{route.name}</h3>
              <p className="text-sm mt-1" data-id="element-250">{route.description}</p>
              <p className="text-sm mt-2" data-id="element-251">
                <span className="font-medium" data-id="element-252">Type:</span>{' '}
                {route.type === 'recovery' ? 'Recovery Route' : 'Mobile Repair Corridor'}
              </p>
              <p className="text-sm mt-1" data-id="element-253">
                <span className="font-medium" data-id="element-254">Status:</span>{' '}
                {currentDay >= 1 ? 'Active' : 'Planned'}
              </p>
            </div>
          </Popup>
        </Polyline>)}
      {/* Render NMC staging zones */}
      {visibleNMCZones.map(zone => <Marker key={`nmc-zone-${zone.id}`} position={zone.coordinates} icon={createNMCStagingZoneIcon(zone.vehiclesNMC)} data-id="element-255">
          <Popup data-id="element-256">
            <div className="min-w-[200px]" data-id="element-257">
              <h3 className="font-bold text-base" data-id="element-258">{zone.name}</h3>
              <p className="text-sm mt-2" data-id="element-259">{zone.description}</p>
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-id="element-260">
                <span className="font-medium" data-id="element-261">NMC Vehicles:</span>{' '}
                {zone.vehiclesNMC}
              </div>
              <p className="text-sm mt-2" data-id="element-262">
                <span className="font-medium" data-id="element-263">Day Established:</span> Day{' '}
                {zone.day}
              </p>
            </div>
          </Popup>
        </Marker>)}
      {/* Render field maintenance teams */}
      {visibleFMTs.map(fmt => <Marker key={`fmt-${fmt.id}`} position={fmt.coordinates} icon={createFieldMaintenanceTeamIcon()} data-id="element-264">
          <Popup data-id="element-265">
            <div className="min-w-[200px]" data-id="element-266">
              <h3 className="font-bold text-base" data-id="element-267">{fmt.name}</h3>
              <p className="text-sm mt-2" data-id="element-268">{fmt.description}</p>
              <div className="mt-2" data-id="element-269">
                <h4 className="font-medium text-sm" data-id="element-270">Capabilities:</h4>
                <ul className="text-sm list-disc pl-5 mt-1" data-id="element-271">
                  {fmt.capabilities.map((capability, index) => <li key={index} data-id="element-272">{capability}</li>)}
                </ul>
              </div>
              <p className="text-sm mt-2" data-id="element-273">
                <span className="font-medium" data-id="element-274">Deployed:</span> Day {fmt.day}
              </p>
            </div>
          </Popup>
        </Marker>)}
      {/* Render maintenance status markers for each day */}
      {sortedSegments.map(segment => {
      const maintenanceStatus = getMaintenanceStatusForDay(segment.day);
      // Get the coordinates for the marker - use the last point of each segment
      const markerPosition = segment.coordinates[segment.coordinates.length - 1];
      // Only show days up to the current selected day
      if (segment.day > currentDay && currentDay !== 0) {
        return null;
      }
      return <Marker key={`maintenance-status-day-${segment.day}`} position={[markerPosition[0] - 0.1, markerPosition[1] - 0.1]} // Offset from other markers
      icon={createMaintenanceStatusIcon(segment.day, maintenanceStatus.percentageFMC, maintenanceStatus.status)} data-id="element-275">
            <Popup data-id="element-276">
              <div className="min-w-[200px]" data-id="element-277">
                <h3 className="font-bold text-base" data-id="element-278">
                  Class VII Maintenance Status - Day {segment.day}
                </h3>
                <div className="mt-2 space-y-2 text-sm" data-id="element-279">
                  <div data-id="element-280">
                    <span className="font-medium" data-id="element-281">Location: </span>
                    {segment.name.split(': ')[1]}
                  </div>
                  <div data-id="element-282">
                    <span className="font-medium" data-id="element-283">Status: </span>
                    <span className={`font-medium ${maintenanceStatus.status === 'green' ? 'text-green-600' : maintenanceStatus.status === 'amber' ? 'text-amber-600' : maintenanceStatus.status === 'red' ? 'text-red-600' : 'text-gray-800'}`} data-id="element-284">
                      {maintenanceStatus.status === 'green' && '🟢 Green'}
                      {maintenanceStatus.status === 'amber' && '🟠 Amber'}
                      {maintenanceStatus.status === 'red' && '🔴 Red'}
                      {maintenanceStatus.status === 'black' && '⚫ Black'}
                    </span>
                  </div>
                  <div data-id="element-285">
                    <span className="font-medium" data-id="element-286">FMC Equipment: </span>
                    {maintenanceStatus.percentageFMC}%
                  </div>
                  <div data-id="element-287">
                    <span className="font-medium" data-id="element-288">Note: </span>
                    {maintenanceStatus.notes}
                  </div>
                  {segment.day === 4 && <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-id="element-289">
                      <span className="font-medium" data-id="element-290">
                        Class VII Status: 🔴 Red
                      </span>
                      <ul className="mt-1 list-disc pl-5" data-id="element-291">
                        <li data-id="element-292">FMC Equipment: 43%</li>
                        <li data-id="element-293">NMC: 9 vehicles (3 LMTVs, 2 MRAPs, 4 trailers)</li>
                        <li data-id="element-294">Field Repair Capability: Degraded</li>
                        <li data-id="element-295">
                          Action: Route recovery assets to Node F1 (Fort
                          Stockton)
                        </li>
                      </ul>
                    </div>}
                  {segment.day === 5 && <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-296">
                      <span className="font-medium" data-id="element-297">Maintenance Complete</span>{' '}
                      - All vehicles restored to operational status
                    </div>}
                </div>
              </div>
            </Popup>
          </Marker>;
    })}
      {/* Render a summary table when clicking on the route */}
      {sortedSegments.length > 0 && <Polyline positions={sortedSegments.flatMap(segment => segment.coordinates)} pathOptions={{
      color: 'transparent',
      weight: 20,
      opacity: 0.01
    }} data-id="element-298">
          <Popup data-id="element-299">
            <div className="min-w-[280px]" data-id="element-300">
              <h3 className="font-bold text-base mb-2" data-id="element-301">
                Class VII Maintenance Status Summary
              </h3>
              <table className="w-full text-sm border-collapse" data-id="element-302">
                <thead data-id="element-303">
                  <tr className="bg-gray-100" data-id="element-304">
                    <th className="border px-2 py-1 text-left" data-id="element-305">Day</th>
                    <th className="border px-2 py-1 text-left" data-id="element-306">% FMC</th>
                    <th className="border px-2 py-1 text-left" data-id="element-307">Status</th>
                    <th className="border px-2 py-1 text-left" data-id="element-308">Notes</th>
                  </tr>
                </thead>
                <tbody data-id="element-309">
                  {dailyMaintenanceStatus.map(status => <tr key={`summary-day-${status.day}`} className={status.day === 5 ? 'bg-green-50' : ''} data-id="element-310">
                      <td className="border px-2 py-1" data-id="element-311">{status.day}</td>
                      <td className="border px-2 py-1 font-medium" data-id="element-312">
                        {status.percentageFMC}%
                        {status.day === 5 && <span className="ml-1 text-xs text-green-600" data-id="element-313">
                            (Resupply)
                          </span>}
                      </td>
                      <td className="border px-2 py-1" data-id="element-314">
                        <span className={`inline-block w-3 h-3 rounded-full mr-1 ${status.status === 'green' ? 'bg-green-600' : status.status === 'amber' ? 'bg-amber-600' : status.status === 'red' ? 'bg-red-600' : 'bg-gray-800'}`} data-id="element-315"></span>
                        {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                      </td>
                      <td className="border px-2 py-1 text-xs" data-id="element-316">
                        {status.notes}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-500" data-id="element-317">
                <div className="font-medium mb-1" data-id="element-318">Color logic:</div>
                <div className="flex items-center" data-id="element-319">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-green-600" data-id="element-320"></span>
                  <span data-id="element-321">🟢 ≥75% FMC</span>
                </div>
                <div className="flex items-center" data-id="element-322">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-amber-600" data-id="element-323"></span>
                  <span data-id="element-324">🟠 50–74% FMC</span>
                </div>
                <div className="flex items-center" data-id="element-325">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-red-600" data-id="element-326"></span>
                  <span data-id="element-327">🔴 25–49% FMC</span>
                </div>
                <div className="flex items-center" data-id="element-328">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-gray-800" data-id="element-329"></span>
                  <span data-id="element-330">⚫ &lt;25% FMC</span>
                </div>
              </div>
            </div>
          </Popup>
        </Polyline>}
    </>;
};